import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api, tokenStore, setUnauthorizedHandler, nativeReady } from '../services/api';

// Session timeout configuration (VAPT compliant)
const SESSION_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours idle timeout
const WARNING_BEFORE_MS = 5 * 60 * 1000; // Show warning 5 minutes before expiry
const WARNING_COUNTDOWN_MS = 60 * 1000; // Auto-logout 60 seconds after warning if no response

interface AuthUser {
  id: string;
  email: string;
}

interface AuthContextType {
  user: AuthUser | null;
  role: string | null;
  profileId: string | null;
  fullName: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; role?: string }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  showSessionWarning: boolean;
  sessionExpiresIn: number;
  extendSession: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  role: null,
  profileId: null,
  fullName: null,
  loading: true,
  signIn: async () => ({ error: null }),
  signOut: async () => {},
  refreshProfile: async () => {},
  showSessionWarning: false,
  sessionExpiresIn: 0,
  extendSession: () => {},
});

export const useAuth = () => useContext(AuthContext);

// Derive a display name from the /auth/me profile based on role.
function computeFullName(role: string, profile: any): string | null {
  if (!profile) return null;
  if (role === 'vendor') return profile.vendor_name ?? null;
  if (role === 'counter_worker') {
    return `${profile.vendor?.vendor_name ?? ''} - ${profile.counter_name ?? ''}`;
  }
  return profile.full_name ?? null;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [fullName, setFullName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Session timeout state
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [sessionExpiresIn, setSessionExpiresIn] = useState(0);
  const lastActivityRef = useRef<number>(Date.now());
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const warningShownRef = useRef<boolean>(false);

  // Clear all auth state + tokens (used by signOut, idle-timeout, and forced 401 logout).
  const clearAuthState = useCallback(() => {
    setUser(null);
    setRole(null);
    setProfileId(null);
    setFullName(null);
    setShowSessionWarning(false);
    setSessionExpiresIn(0);
    warningShownRef.current = false;
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    tokenStore.clear();
  }, []);

  // Reset activity timestamp on user interaction
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (showSessionWarning) {
      setShowSessionWarning(false);
      setSessionExpiresIn(0);
      warningShownRef.current = false;
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }
  }, [showSessionWarning]);

  // Extend session (called from warning dialog)
  const extendSession = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowSessionWarning(false);
    setSessionExpiresIn(0);
    warningShownRef.current = false;
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    const { data, error } = await api.auth.me();
    if (error) {
      // Keep existing state; avoid forced logout on transient failures.
      return;
    }
    if (data?.profile) {
      setRole(data.role);
      setProfileId(data.profile.id);
      setFullName(computeFullName(data.role, data.profile));
      setUser({ id: data.profile.id, email: data.profile.email });
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await api.auth.login(email, password);
    if (error || !data) {
      return { error: error || new Error('Login failed') };
    }
    tokenStore.set(data.accessToken);
    setUser({ id: data.user.id, email: data.user.email });
    setRole(data.user.role);
    setProfileId(data.user.profileId);
    setFullName(data.user.fullName);
    lastActivityRef.current = Date.now();
    return { error: null, role: data.user.role };
  }, []);

  const signOut = useCallback(async () => {
    try { await api.auth.logout(); } catch { /* ignore */ }
    clearAuthState();
  }, [clearAuthState]);

  // Restore the session on mount. There's no access token in memory yet, so
  // /auth/me will 401 and the API layer silently refreshes from the HttpOnly
  // cookie; if there's no valid cookie, we simply land logged-out.
  useEffect(() => {
    let mounted = true;
    (async () => {
      await nativeReady; // hydrate native refresh token before restoring session
      const { data, error } = await api.auth.me();
      if (!mounted) return;
      if (error || !data?.profile) {
        setLoading(false);
        return;
      }
      setRole(data.role);
      setProfileId(data.profile.id);
      setFullName(computeFullName(data.role, data.profile));
      setUser({ id: data.profile.id, email: data.profile.email });
      lastActivityRef.current = Date.now();
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Force logout if a request fails auth even after refresh.
  useEffect(() => {
    setUnauthorizedHandler(() => clearAuthState());
    return () => setUnauthorizedHandler(null);
  }, [clearAuthState]);

  // Session timeout check - runs every 30 seconds when user is logged in
  useEffect(() => {
    if (!user) return;

    const checkSessionTimeout = () => {
      const idleTime = Date.now() - lastActivityRef.current;
      const timeUntilExpiry = SESSION_TIMEOUT_MS - idleTime;

      if (idleTime >= SESSION_TIMEOUT_MS) {
        clearAuthState();
        return;
      }
      if (timeUntilExpiry <= WARNING_BEFORE_MS && !warningShownRef.current) {
        warningShownRef.current = true;
        setShowSessionWarning(true);
        setSessionExpiresIn(Math.floor(timeUntilExpiry / 1000));
      }
    };

    const intervalId = setInterval(checkSessionTimeout, 30000);
    return () => clearInterval(intervalId);
  }, [user, clearAuthState]);

  // Countdown when warning is active
  useEffect(() => {
    if (!showSessionWarning || !user) return;

    const countdownInterval = setInterval(() => {
      setSessionExpiresIn(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const logoutTimer = setTimeout(() => {
      clearAuthState();
    }, WARNING_COUNTDOWN_MS);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(logoutTimer);
    };
  }, [showSessionWarning, user, clearAuthState]);

  // Track user activity (mouse, keyboard, scroll, clicks)
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    let lastUpdate = 0;
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastUpdate > 5000) {
        lastUpdate = now;
        resetActivity();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        lastActivityRef.current = Date.now();
        if (warningShownRef.current) {
          setShowSessionWarning(false);
          setSessionExpiresIn(0);
          warningShownRef.current = false;
        }
        if (logoutTimerRef.current) {
          clearTimeout(logoutTimerRef.current);
          logoutTimerRef.current = null;
        }
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
      }
    };

    events.forEach(event => window.addEventListener(event, throttledReset, { passive: true }));
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      events.forEach(event => window.removeEventListener(event, throttledReset));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, resetActivity]);

  return (
    <AuthContext.Provider value={{
      user, role, profileId, fullName, loading, signIn, signOut, refreshProfile,
      showSessionWarning, sessionExpiresIn, extendSession
    }}>
      {children}
    </AuthContext.Provider>
  );
};
